import React, { useState, useRef, useCallback, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import { CircularProgress } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CoachingKangaroo from '../../../api/CoachingKangaroo';

export const BRAND_PURPLE = 'rgb(102, 41, 87)';

export const NLP_DETAIL_SPACER_COLUMNS = 1;
export const NLP_DETAIL_CONTENT_COLUMNS = 9;
export const NLP_DETAIL_SCORE_COLUMNS = 2;
export const LLM_DETAIL_ICON_COLUMNS = 1;
export const LLM_DETAIL_CONTENT_COLUMNS = 7;

export const getDetailRowLayout = (detailLayout = 'llm') => {
    if (detailLayout === 'nlp') {
        return {
            spacerColumns: NLP_DETAIL_SPACER_COLUMNS,
            contentColumns: NLP_DETAIL_CONTENT_COLUMNS,
            trailingColumns: NLP_DETAIL_SCORE_COLUMNS,
        };
    }

    return {
        spacerColumns: LLM_DETAIL_ICON_COLUMNS,
        contentColumns: LLM_DETAIL_CONTENT_COLUMNS,
        trailingColumns: 0,
    };
};

export const renderDetailRow = ({
    content,
    trailing = null,
    spacer = null,
    spacerColumns = NLP_DETAIL_SPACER_COLUMNS,
    contentColumns = NLP_DETAIL_CONTENT_COLUMNS,
    trailingColumns = NLP_DETAIL_SCORE_COLUMNS,
    alignItems = 'center',
    marginTop,
}) => (
    <Grid
        container
        alignItems={alignItems}
        style={marginTop !== undefined ? { marginTop } : undefined}
    >
        <Grid item xs={spacerColumns}>
            {spacer}
        </Grid>
        <Grid item xs={contentColumns}>
            {content}
        </Grid>
        {trailingColumns > 0 && (
            <Grid item xs={trailingColumns}>
                {trailing}
            </Grid>
        )}
    </Grid>
);

export const formatDuration = (ms) => {
    if (ms === null || ms === undefined || Number.isNaN(ms)) {
        return '—';
    }
    if (ms < 1000) {
        return `${Math.round(ms)}ms`;
    }
    return `${(ms / 1000).toFixed(2)}s`;
};

export const safeStringify = (data) => {
    if (data === null || data === undefined) {
        return '';
    }
    try {
        return JSON.stringify(data, null, 2);
    } catch {
        return String(data);
    }
};

export const handleKeyActivate = (event, callback) => {
    if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        callback();
    }
};

export const JsonView = ({ data }) => {
    const text = safeStringify(data);
    if (!text) {
        return null;
    }
    return <pre className="detail-json">{text}</pre>;
};

export const DetailSection = ({ title, children, defaultExpanded = true }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    const toggle = () => setExpanded((prev) => !prev);

    return (
        <div className="detail-section">
            <div
                className="detail-section-header"
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onClick={toggle}
                onKeyDown={(e) => handleKeyActivate(e, toggle)}
            >
                {title}
                {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </div>
            {expanded && <div className="detail-section-content">{children}</div>}
        </div>
    );
};

export const TracePanelHeader = ({ title, countLabel, isExpanded, onToggle, disabled }) => {
    const chevron = isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />;

    if (disabled) {
        return (
            <div className="trace-header trace-header-disabled" aria-disabled="true">
                <div className="trace-header-title">
                    {chevron}
                    {title}
                </div>
                <div className="trace-header-count">{countLabel}</div>
            </div>
        );
    }

    return (
        <button
            type="button"
            className="trace-header"
            onClick={onToggle}
            aria-expanded={isExpanded}
        >
            <div className="trace-header-title">
                {chevron}
                {title}
            </div>
            <div className="trace-header-count">{countLabel}</div>
        </button>
    );
};

export const TraceDetailTabs = ({ tabs, activeTab, onTabChange }) => (
    <div className="detail-tabs" role="tablist">
        {tabs.map(({ id, label }) => (
            <button
                key={id}
                type="button"
                role="tab"
                aria-selected={activeTab === id}
                className={`detail-tab ${activeTab === id ? 'active' : ''}`}
                onClick={() => onTabChange(id)}
            >
                {label}
            </button>
        ))}
    </div>
);

export const TraceSelectableRow = ({
    className,
    isSelected,
    onSelect,
    style,
    children
}) => (
    <div
        className={`trace-node-row ${isSelected ? 'selected' : ''} ${className || ''}`}
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        style={style}
        onClick={onSelect}
        onKeyDown={(e) => handleKeyActivate(e, onSelect)}
    >
        {children}
    </div>
);

export const formatSlavicCount = (count, forms, t) => {
    if (count === 1) {
        return `1 ${t(forms.one)}`;
    }
    if (count >= 2 && count <= 4) {
        return `${count} ${t(forms.few)}`;
    }
    return `${count} ${t(forms.many)}`;
};

export const formatCountLabel = (count, t, i18n, entity) => {
    const language = i18n?.language || 'cz';
    if (language.startsWith('en')) {
        if (entity === 'node') {
            return `${count} ${t(count === 1 ? 'node_one' : 'nodes')}`;
        }
        return `${count} ${t(count === 1 ? 'run_one' : 'runs')}`;
    }
    if (entity === 'node') {
        return formatSlavicCount(count, { one: 'node_one', few: 'node_few', many: 'node_many' }, t);
    }
    return formatSlavicCount(count, { one: 'run_one', few: 'run_few', many: 'run_many' }, t);
};

export const buildAnswerSubresourceUrl = (projectId, conversationId, answerId, resource) =>
    '/api/training/v1/' +
    encodeURIComponent(projectId) +
    '/conversation/' +
    encodeURIComponent(conversationId) +
    '/answer/' +
    encodeURIComponent(answerId) +
    '/' +
    resource;

const isAbortError = (error) =>
    error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';

export const resolvePanelLoadErrorMessage = (error, t) => {
    if (isAbortError(error)) {
        return null;
    }
    if (!error?.response) {
        return t('panel_load_network_error');
    }
    return t('panel_load_failed');
};

export const PanelLoadingState = () => (
    <div
        style={{ padding: '24px', display: 'flex', justifyContent: 'center', width: '100%' }}
        aria-busy="true"
    >
        <CircularProgress size={24} />
    </div>
);

export const PanelLoadError = ({ message, onRetry, retryLabel }) => (
    <div
        role="alert"
        style={{
            padding: '24px',
            color: '#d93025',
            fontSize: '13px',
            width: '100%',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px'
        }}
    >
        <div>{message}</div>
        <button type="button" className="trace-header" onClick={onRetry}>
            {retryLabel}
        </button>
    </div>
);

export const useLazyAnswerPanelData = ({
    projectId,
    conversationId,
    answerId,
    resource,
    t,
    collapseWhenEmpty = false
}) => {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const requestIdRef = useRef(0);
    const abortControllerRef = useRef(null);

    const isLoaded = data !== null;
    const hasData = isLoaded && data.length > 0;

    const fetchData = useCallback(() => {
        if (!projectId || !conversationId || !answerId) {
            return;
        }

        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;
        const requestId = ++requestIdRef.current;

        setIsLoading(true);
        setLoadError(null);

        CoachingKangaroo.get(buildAnswerSubresourceUrl(projectId, conversationId, answerId, resource), {
            signal: controller.signal
        })
            .then((response) => {
                if (requestId !== requestIdRef.current) {
                    return;
                }
                const loaded = Array.isArray(response.data) ? response.data : [];
                setData(loaded);
                setIsLoading(false);
                if (loaded.length === 0 && collapseWhenEmpty) {
                    setIsExpanded(false);
                }
            })
            .catch((error) => {
                if (requestId !== requestIdRef.current || isAbortError(error)) {
                    return;
                }
                setLoadError(resolvePanelLoadErrorMessage(error, t));
                setIsLoading(false);
            });
    }, [projectId, conversationId, answerId, resource, t, collapseWhenEmpty]);

    useEffect(() => {
        abortControllerRef.current?.abort();
        requestIdRef.current += 1;
        setData(null);
        setLoadError(null);
        setIsLoading(false);
        setIsExpanded(false);

        return () => abortControllerRef.current?.abort();
    }, [projectId, conversationId, answerId]);

    const handleToggle = useCallback(() => {
        const willExpand = !isExpanded;
        setIsExpanded(willExpand);
        if (willExpand && data === null && !isLoading) {
            fetchData();
        }
    }, [isExpanded, data, isLoading, fetchData]);

    return {
        data,
        isLoading,
        loadError,
        isExpanded,
        isLoaded,
        hasData,
        handleToggle,
        fetchData,
        setIsExpanded
    };
};
